import { Component, NgZone } from '@angular/core';
import {
  EMPTY,
  Observable,
  combineLatest,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
  zip,
} from 'rxjs';
import { AuthService } from './auth/auth.service';
import { PlatformService } from './services/platform.service';
import { AppLanguage, LanguageService } from './services/language.service';
import moment from 'moment';
import { CartService } from './services/cart.service';
import { ClientService } from './services/client.service';
import { EventType, NavigationEnd, Router } from '@angular/router';
import { StorageKeys, StorageService } from './services/storage.service';
import { register as registerSwiperJS } from 'swiper/element/bundle';
import { SplashScreen } from '@capacitor/splash-screen';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { environment } from 'src/environments/environment';
import { NavController } from '@ionic/angular';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  public readonly currentRoute$: Observable<string>;
  public readonly shouldDisplayHeader$: Observable<boolean>;
  public readonly shouldDisplayFooter$: Observable<boolean>;
  public readonly isDevMode: boolean;

  public readonly routesWithoutHeader: string[] = [
    'profile',
    'orders',
    'about',
    'subsidies',
    'terms-of-use',
    'imprint',
  ];

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly platformService: PlatformService,
    private readonly languageService: LanguageService,
    private readonly cartService: CartService,
    private readonly clientService: ClientService,
    private readonly storageService: StorageService,
    private readonly zone: NgZone,
    private readonly navCtrl: NavController
  ) {
    SplashScreen.show();
    this.subscribeForDeepLinking();

    this.currentRoute$ = this.router.events.pipe(
      filter((e) => e.type === EventType.NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects?.replace('/', '')),
      shareReplay(1)
    );

    this.shouldDisplayHeader$ = combineLatest([
      this.authService.isAuthed$.pipe(shareReplay(1)),
      this.currentRoute$,
    ]).pipe(
      map(([isAuthed, route]) => {
        return (
          isAuthed &&
          !this.routesWithoutHeader.some(
            (r) => new RegExp(r).exec(route)?.length! > 0
          )
        );
      })
    );

    this.shouldDisplayFooter$ = this.authService.isAuthed$;
    this.isDevMode = this.platformService.isDebug();
    this.storageService
      .get<AppLanguage>(StorageKeys.LANGUAGE)
      .subscribe((lang) => {
        if (lang === null) {
          lang = this.languageService.defaultLanguage;
        }
        this.languageService.setLanguage(lang);
      });
    moment.locale('en-gb');
    moment.updateLocale('en-gb', {
      week: {
        dow: 1,
      },
    });
    this.cartService.init();
    this.authService.isAuthed$
      .pipe(
        switchMap((isAuthed) => (isAuthed ? this.clientService.init() : EMPTY))
      )
      .subscribe();
    registerSwiperJS();
  }

  private subscribeForDeepLinking(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        const domain = environment.domain;
        const slug = event.url.split(`${domain}/`).pop();
        if (slug) {
          await this.navCtrl.navigateRoot('store');
          await this.navCtrl.navigateRoot(slug);
          return;
        }
      });
    });
  }
}
